import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { PageHeader } from "@/components/PageHeader";
import { KpiCard } from "@/components/KpiCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Building2, CheckCircle2, Clock, XCircle, IndianRupee, Milk, Users, HardDrive,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, LineChart, Line,
} from "recharts";

const COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

export default function PlatformDashboard() {
  const { t } = useI18n();
  const { data, isLoading } = useQuery({
    queryKey: ["platform-dashboard"],
    queryFn: async () => (await api.get("/platform/dashboard")).data,
  });

  if (isLoading || !data)
    return (
      <div className="space-y-6">
        <PageHeader title={t("pd.title")} breadcrumb={[t("crumb.platform"), t("nav.dashboard")]} />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
      </div>
    );

  const kpis = [
    { label: t("pd.totalTenants"), value: data.total_tenants, icon: Building2, accent: "primary" },
    { label: t("pd.active"), value: data.active_tenants, icon: CheckCircle2, accent: "primary" },
    { label: t("pd.trial"), value: data.trial_tenants, icon: Clock, accent: "warning" },
    { label: t("pd.expired"), value: data.expired_tenants, icon: XCircle, accent: "danger" },
    { label: t("pd.monthlyRevenue"), value: `₹${data.monthly_revenue.toLocaleString("en-IN")}`, icon: IndianRupee, accent: "primary" },
    { label: t("pd.totalCollections"), value: data.total_collections.toLocaleString("en-IN"), icon: Milk, accent: "secondary" },
    { label: t("pd.totalFarmers"), value: data.total_farmers.toLocaleString("en-IN"), icon: Users, accent: "secondary" },
    { label: t("pd.storage"), value: `${data.storage_usage_mb} MB`, icon: HardDrive, accent: "warning" },
  ];

  return (
    <div className="space-y-6" data-testid="platform-dashboard">
      <PageHeader title={t("pd.title")} subtitle={t("pd.subtitle")} breadcrumb={[t("crumb.platform"), t("nav.dashboard")]} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((k, i) => <KpiCard key={i} {...k} index={i} testId={`kpi-${["total","active","trial","expired","revenue","collections","farmers","storage"][i]}`} />)}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="font-heading text-lg">{t("pd.growth")}</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={data.tenant_growth}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                <Line type="monotone" dataKey="tenants" stroke="hsl(var(--chart-1))" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="font-heading text-lg">{t("pd.plans")}</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data.plan_distribution} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={3}>
                  {data.plan_distribution.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-2 flex flex-wrap justify-center gap-3">
              {data.plan_distribution.map((e, i) => (
                <span key={i} className="flex items-center gap-1.5 text-xs">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} /> {e.name} ({e.value})
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="font-heading text-lg">{t("pd.statusDist")}</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.status_distribution}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
              <Tooltip cursor={{ fill: "hsl(var(--muted))" }} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} fill="hsl(var(--chart-1))" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
